<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useMeta } from '@/stores/meta.js'
import { conditionFields, OPERATORS_BY_TYPE, NO_VALUE } from '@/lib/conditionFields.js'
import RefPicker from './RefPicker.vue'
import OwnerPicker from './OwnerPicker.vue'
import MultiSelect from './MultiSelect.vue'
import NumberInput from '@/components/form/NumberInput.vue'

/**
 * Builds a condition in the shared rules language (field rules, assignment, sequences,
 * offline scope, advanced list filters): groups of "all"/"any", "not", and leaves
 * {field, op, value}. Nested groups call this component again.
 */
defineOptions({ name: 'ConditionBuilder' })
const props = defineProps({
  entity: { type: String, required: true },
  modelValue: { type: Object, default: null },
  depth: { type: Number, default: 0 },
  disabled: Boolean,
})
const emit = defineEmits(['update:modelValue', 'remove'])
const { t } = useI18n()
const meta = useMeta()
const fields = computed(() => conditionFields(props.entity, meta, t))

// The top level is always a group, so the person can add to it.
const group = computed(() => {
  const c = props.modelValue
  if (!c || Object.keys(c).length === 0) return { mode: 'all', negate: false, children: [] }
  if (c.not) {
    const inner = c.not.all || c.not.any ? c.not : { all: [c.not] }
    return { mode: inner.any ? 'any' : 'all', negate: true, children: inner.any || inner.all || [] }
  }
  if (c.all || c.any) return { mode: c.any ? 'any' : 'all', negate: false, children: c.any || c.all }
  return { mode: 'all', negate: false, children: [c] }
})

function emitGroup(mode, negate, children) {
  if (children.length === 0 && props.depth === 0) return emit('update:modelValue', null)
  const inner = { [mode]: children }
  emit('update:modelValue', negate ? { not: inner } : inner)
}
const setMode = (mode) => emitGroup(mode, group.value.negate, group.value.children)
const setNegate = (negate) => emitGroup(group.value.mode, negate, group.value.children)
function setChild(i, child) {
  const children = [...group.value.children]
  if (child === null) children.splice(i, 1)
  else children[i] = child
  emitGroup(group.value.mode, group.value.negate, children)
}
function addLeaf() {
  const first = fields.value[0]
  emitGroup(group.value.mode, group.value.negate, [...group.value.children, { field: first?.value || 'name', op: 'eq', value: null }])
}
function addGroup() {
  emitGroup(group.value.mode, group.value.negate, [...group.value.children, { any: [] }])
}

const isGroup = (c) => c && (c.all || c.any || c.not)
const fieldOf = (name) => fields.value.find((f) => f.value === name) || { type: 'text', label: name }
const opsFor = (name) => OPERATORS_BY_TYPE[fieldOf(name).type] || OPERATORS_BY_TYPE.text

function setField(i, leaf, field) {
  const ops = opsFor(field)
  setChild(i, { field, op: ops.includes(leaf.op) ? leaf.op : ops[0], value: null })
}
function setOp(i, leaf, op) {
  const multi = ['in', 'nin'].includes(op)
  let value = leaf.value
  if (NO_VALUE.includes(op)) value = undefined
  else if (op === 'between') value = Array.isArray(value) ? value : [null, null]
  else if (multi) value = Array.isArray(value) ? value : value != null ? [value] : []
  else if (Array.isArray(value)) value = value[0] ?? null
  const next = { field: leaf.field, op }
  if (value !== undefined) next.value = value
  setChild(i, next)
}
function setValue(i, leaf, value) {
  setChild(i, { ...leaf, value })
}
function numberOrText(type, raw) {
  if (raw === '') return null
  if (type === 'number') {
    const n = Number(String(raw).replace(',', '.'))
    return Number.isNaN(n) ? raw : n
  }
  return raw
}
const boolValue = (v) => (v === true ? 'true' : v === false ? 'false' : '')
</script>

<template>
  <div :class="['space-y-2 rounded-lg', depth > 0 ? 'border border-dashed border-default p-2' : '']">
    <div class="flex flex-wrap items-center gap-2 text-sm">
      <label class="inline-flex items-center gap-1 text-xs text-muted">
        <input type="checkbox" class="accent-brand-600" :checked="group.negate" :disabled="disabled" @change="setNegate($event.target.checked)" /> {{ $t('settings.not') }}
      </label>
      <select class="input w-auto py-1 text-xs" :value="group.mode" :disabled="disabled" @change="setMode($event.target.value)">
        <option value="all">{{ $t('settings.matchAll') }}</option>
        <option value="any">{{ $t('settings.matchAny') }}</option>
      </select>
      <span class="flex-1"></span>
      <button v-if="depth > 0 && !disabled" type="button" class="text-faint hover:text-red-600" :aria-label="$t('common.remove')" @click="emit('remove')"><i class="fa-solid fa-xmark" aria-hidden="true"></i></button>
    </div>

    <div v-for="(child, i) in group.children" :key="i">
      <ConditionBuilder v-if="isGroup(child)" :entity="entity" :model-value="child" :depth="depth + 1" :disabled="disabled" @update:model-value="(c) => setChild(i, c ?? { any: [] })" @remove="setChild(i, null)" />
      <div v-else class="flex flex-wrap items-start gap-2">
        <select class="input min-w-40 flex-1 py-1.5 text-sm" :value="child.field" :disabled="disabled" :aria-label="$t('settings.field')" @change="setField(i, child, $event.target.value)">
          <option v-if="!fields.some((f) => f.value === child.field)" :value="child.field">{{ child.field }}</option>
          <option v-for="f in fields" :key="f.value" :value="f.value">{{ f.label }}</option>
        </select>
        <select class="input w-44 py-1.5 text-sm" :value="child.op" :disabled="disabled" :aria-label="$t('settings.operator')" @change="setOp(i, child, $event.target.value)">
          <option v-for="op in opsFor(child.field)" :key="op" :value="op">{{ $t(`settings.ops.${op}`) }}</option>
        </select>
        <template v-if="!NO_VALUE.includes(child.op)">
          <div class="min-w-40 flex-1">
            <MultiSelect v-if="fieldOf(child.field).type === 'enum' && ['in', 'nin'].includes(child.op)" :model-value="child.value || []" :options="fieldOf(child.field).options" :disabled="disabled" @update:model-value="(v) => setValue(i, child, v)" />
            <select v-else-if="fieldOf(child.field).type === 'enum'" class="input py-1.5 text-sm" :value="child.value ?? ''" :disabled="disabled" @change="setValue(i, child, $event.target.value || null)">
              <option value="">—</option>
              <option v-for="o in fieldOf(child.field).options" :key="o.value" :value="o.value">{{ o.label }}</option>
            </select>
            <select v-else-if="fieldOf(child.field).type === 'bool'" class="input py-1.5 text-sm" :value="boolValue(child.value)" :disabled="disabled" @change="setValue(i, child, $event.target.value === 'true')">
              <option value="true">{{ $t('common.yes') }}</option>
              <option value="false">{{ $t('common.no') }}</option>
            </select>
            <OwnerPicker v-else-if="fieldOf(child.field).type === 'owner'" :model-value="child.value ? { type: 'user', uuid: child.value, label: '…' } : null" :disabled="disabled" @update:model-value="(o) => setValue(i, child, o?.uuid ?? null)" />
            <select v-else-if="fieldOf(child.field).pipeline === 'pipeline'" class="input py-1.5 text-sm" :value="child.value ?? ''" :disabled="disabled" @change="setValue(i, child, $event.target.value || null)">
              <option v-for="p in meta.pipelines(entity)" :key="p.uuid" :value="p.uuid">{{ meta.localized(p.name) }}</option>
            </select>
            <select v-else-if="fieldOf(child.field).pipeline === 'pipeline_stage'" class="input py-1.5 text-sm" :value="child.value ?? ''" :disabled="disabled" @change="setValue(i, child, $event.target.value || null)">
              <template v-for="p in meta.pipelines(entity)" :key="p.uuid">
                <option v-for="s in p.stages" :key="s.uuid" :value="s.uuid">{{ meta.localized(p.name) }} · {{ meta.localized(s.name) }}</option>
              </template>
            </select>
            <RefPicker :allow-create="false" v-else-if="fieldOf(child.field).type === 'ref' && fieldOf(child.field).ref && child.op !== 'in'" :entity="fieldOf(child.field).ref" :model-value="child.value" :disabled="disabled" @update:model-value="(v) => setValue(i, child, v)" />
            <div v-else-if="child.op === 'between'" class="flex gap-2">
              <input class="input py-1.5 text-sm" :type="fieldOf(child.field).type === 'date' ? 'date' : 'text'" :value="child.value?.[0] ?? ''" :disabled="disabled" @change="setValue(i, child, [numberOrText(fieldOf(child.field).type, $event.target.value), child.value?.[1] ?? null])" />
              <input class="input py-1.5 text-sm" :type="fieldOf(child.field).type === 'date' ? 'date' : 'text'" :value="child.value?.[1] ?? ''" :disabled="disabled" @change="setValue(i, child, [child.value?.[0] ?? null, numberOrText(fieldOf(child.field).type, $event.target.value)])" />
            </div>
            <NumberInput v-else-if="['within_last_days', 'within_next_days', 'older_than_days'].includes(child.op)" integer :min="0" class="py-1.5 text-sm" :model-value="child.value ?? null" :disabled="disabled" @change="(v) => setValue(i, child, v ?? 0)" />
            <NumberInput v-else-if="['number', 'integer', 'decimal', 'money', 'percent'].includes(fieldOf(child.field).type)" :integer="fieldOf(child.field).type === 'integer'" class="py-1.5 text-sm" :model-value="child.value ?? null" :disabled="disabled" @change="(v) => setValue(i, child, v)" />
            <input v-else class="input py-1.5 text-sm" :type="fieldOf(child.field).type === 'date' ? 'date' : 'text'" :value="child.value ?? ''" :disabled="disabled" @change="setValue(i, child, numberOrText(fieldOf(child.field).type, $event.target.value))" />
          </div>
        </template>
        <button v-if="!disabled" type="button" class="mt-1.5 text-faint hover:text-red-600" :aria-label="$t('common.remove')" @click="setChild(i, null)"><i class="fa-solid fa-trash-can" aria-hidden="true"></i></button>
      </div>
    </div>

    <div v-if="!disabled" class="flex gap-3 text-sm">
      <button type="button" class="link" @click="addLeaf"><i class="fa-solid fa-plus text-xs" aria-hidden="true"></i> {{ $t('settings.addCondition') }}</button>
      <button v-if="depth < 2" type="button" class="link" @click="addGroup"><i class="fa-solid fa-layer-group text-xs" aria-hidden="true"></i> {{ $t('settings.addGroup') }}</button>
    </div>
  </div>
</template>
